import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FaListAlt, FaTrashAlt, FaRegEdit } from 'react-icons/fa';
import Select from 'react-select';
import * as Motion from 'motion/react';
import LeaderBoardEntriesTable from '../../components/LeaderboardEntriesTable'
import Loader from '../../components/Loaders';
import useDialogBox from '../../hooks/useDialogBox';
import { deleteLeaderboard, getLeaderboard, getLeaderboardEntries, updateLeaderboard } from '../../api/v1/leaderboards';
import { getLeagues } from '../../api/v1/leagues';
import { animationStaggeredItem } from '../../animations';
import '../../styles/screen.scss';

const { motion } = Motion;


const Leaderboard = () => {
  const formRef = useRef(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [leaderboardEntries, setLeaderboardEntries] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const { resetDialogBox, setDialogBox } = useDialogBox();
  const navigate = useNavigate();
  const { leaderboardId } = useParams();

  const loadLeaderboard = async (id) => {
    const leaderboardResult = await getLeaderboard(id);
    const leaderboardEntriesResult = await getLeaderboardEntries(id);

    setLeaderboard(leaderboardResult?.data);
    setLeaderboardEntries(leaderboardEntriesResult?.data);
  };

  const confirmDeleteLeaderboard = async (leaderboardId) => {
    setDialogBox({
      title: 'Delete Leaderboard',
      content: <p>Do you want to delete leaderboard <b>{leaderboard?.displayName || leaderboardId}</b>?</p>,
      actions: [
        {
          title: 'Delete Leaderboard',
          type: 'danger',
          action: async () => await handleDeleteLeaderboard(leaderboardId)
        },
        { title: 'Cancel', type: 'cancel', action: () => resetDialogBox() }
      ]
    });
  };

  const handleDeleteLeaderboard = async (leaderboardId) => {
    await deleteLeaderboard(leaderboardId);
    resetDialogBox();

    navigate(-1);
  };

  const editLeaderboardSubmit = async () => {
    const { leagues, displayName } = formRef.current?.getValues();

    if (!displayName.length) {
      alert('Please enter a displayName');
    }
    else {
      await updateLeaderboard(leaderboardId, {
        displayName,
        leagues
      });

      await loadLeaderboard(leaderboardId);
      resetDialogBox();
    }
  };

  const editLeaderboard = async (l) => {
    setDialogBox({
      title: 'Edit Leaderboard',
      content: <EditLeaderboardForm ref={formRef} leaderboard={l}/>,
      actions: [
        { title: 'Save Leaderboard', type: 'primary', action: () => editLeaderboardSubmit() },
        { title: 'Cancel', type: 'cancel', action: () => resetDialogBox() }
      ]
    });
  };

  useEffect(() => {
    (async () => {
      await loadLeaderboard(leaderboardId);
      setIsLoading(false);
    })();
  }, [leaderboardId]);

  return (
    <>
      <Motion.AnimatePresence>
        {isLoading && <Loader/>}
      </Motion.AnimatePresence>

      <div className="screen-navigation">
        <button className="screen-back-button" onClick={() => navigate(-1)}>&larr; Go Back</button>
      </div>

      <motion.div variants={animationStaggeredItem} className="screen-header">
        <div className="screen-title-wrapper">
          <div>
            <h2 className="screen-title">
              {leaderboard?.displayName}
            </h2>

            <p className="screen-subtitle">
              <b>Leaderboard Slug:</b> {leaderboard?.leaderboardSlug}
            </p>
          </div>
        </div>

        <button className="screen-header-button secondary" onClick={() => editLeaderboard(leaderboard)}>
          <FaRegEdit/>
          Edit Leaderboard
        </button>

        <button className="screen-header-button danger" onClick={() => confirmDeleteLeaderboard(leaderboardId)}>
          <FaTrashAlt/>
          Delete Leaderboard
        </button>
      </motion.div>

      <motion.div variants={animationStaggeredItem} className="screen-section">
        <p>
          <b>Conductor ID:</b>
          &nbsp;
          {leaderboard?.id}
        </p>
      </motion.div>

      <motion.div variants={animationStaggeredItem} className="screen-section flex-3 scroll-overflow">
        <div className="section-heading-wrapper">
          <h3 className="section-heading">
            <FaListAlt />
            Entries
          </h3>
        </div>

        {
          leaderboardEntries.length ?
            <LeaderBoardEntriesTable entries={leaderboardEntries}/> :
            <p className="text-vertically-align text-center text-secondary">No leaderboard entries</p>
        }
      </motion.div>
    </>
  )
};


const EditLeaderboardForm = forwardRef((props, ref) => {
  const [displayName, setDisplayName] = useState(props?.leaderboard?.displayName);
  const [availableLeagues, setAvailableLeagues] = useState([]);
  const [selectedLeagues, setSelectedLeagues] = useState([]);

  const getValues = () => {
    const leagues = selectedLeagues ? selectedLeagues.map(opt => opt.value) : [];
    return { leagues, displayName };
  };

  const handleLeagueSelectChange = (selectedOptions) => {
    setSelectedLeagues(selectedOptions || []);
  };

  useEffect(() => {
    (async () => {
      const result = await getLeagues();
      setAvailableLeagues(result?.data);
    })();
  }, []);

  useEffect(() => {
    const leaderboardExistingLeagues = props?.leaderboard?.Leagues ? props?.leaderboard?.Leagues.map(opt => ({
      value: opt.id,
      label: opt.displayName
    })) : [];

    setSelectedLeagues(leaderboardExistingLeagues);
  }, []);

  useImperativeHandle(ref, () => ({
    getValues,
  }));

  return (
    <div className="modal-form">
      <div className="form-group">
        <label>Display Name</label>
        <input placeholder="Display Name" value={displayName} onChange={(e) => setDisplayName(e.target.value)}/>
      </div>

      <div className="form-group">
        <label>Leagues</label>
        <Select
          value={selectedLeagues}
          onChange={handleLeagueSelectChange}
          isMulti={true}
          placeholder={'Select leagues'}
          options={
            availableLeagues?.map((availableLeague, index) => ({
              value: availableLeague?.id,
              label: availableLeague?.displayName,
            }))
          }
          styles={{
            container: (provided) => ({
              ...provided,
              borderRadius: '0.4rem',
              borderColor: '#0c2659',
              fontFamily: '"Open Sans", sans-serif',
              fontSize: '14px',
              fontWeight: 500,
            }),
            control: (provided, state) => ({
              ...provided,
              backgroundColor: '#06132c',
              borderColor: '#091d42',
              color: 'white',
              cursor: 'text',
              padding: '0.4rem',
              '&:hover': {
                borderColor: '#091d42',
              },
            }),
            menu: (provided) => ({
              ...provided,
              backgroundColor: '#06132c',
              borderRadius: '0.6rem',
            }),
            multiValue: (provided) => ({
              ...provided,
              backgroundColor: '#3b82f6',
              borderRadius: '0.4rem',
              padding: '0.2rem 0.4rem',
            }),
            multiValueLabel: (provided) => ({
              ...provided,
              color: '#ffffff',
              fontSize: '0.8rem',
            }),
            option: (provided, state) => ({
              ...provided,
              backgroundColor: state.isSelected
                ? '#ffffff'
                : state.isFocused
                  ? '#3b82f6'
                  : '#06132c',
              cursor: 'pointer',
              color: state.isSelected
                ? 'white'
                : state.isFocused
                  ? '#ffffff' // Text color on hover
                  : '#white',
            }),
            input: (provided) => ({
              ...provided,
              backgroundColor: '#091d42',
              color: '#ffffff',
            }),
            singleValue: (provided) => ({
              ...provided,
              color: '#3b82f6', // Keeps the final selected text Blue
            }),
          }}
        />
      </div>
    </div>
  );
});

export default Leaderboard;
